/* TITAN WALLS · Reading Room Runtime v5.1
   Continuous page observation, measured sticky geometry and accessible fullscreen reading. */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initializedReaders = new WeakMap();
  const initializedHeroes = new WeakMap();
  const boundOpeners = new WeakSet();
  const isDesignMode = () => document.documentElement.classList.contains('shopify-design-mode');
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

  const setHeroActive = (hero, index) => {
    if (!hero) return;

    const sheets = [...hero.querySelectorAll('[data-reading-hero-sheet]')];
    const selectors = [...hero.querySelectorAll('[data-reading-hero-select]')];
    if (!sheets.length) return;

    const safeIndex = Math.max(0, Math.min(index, sheets.length - 1));
    const activeSheet = sheets[safeIndex];
    const source = activeSheet?.dataset.readingHeroSource || '';
    const title = activeSheet?.dataset.readingHeroTitle || '';

    sheets.forEach((sheet, sheetIndex) => {
      const active = sheetIndex === safeIndex;
      sheet.classList.toggle('is-active', active);
      sheet.hidden = !active;
    });

    selectors.forEach((selector, selectorIndex) => {
      if (selectorIndex === safeIndex) selector.setAttribute('aria-current', 'true');
      else selector.removeAttribute('aria-current');
    });

    hero.querySelectorAll('[data-reading-hero-current]').forEach((node) => {
      node.textContent = String(safeIndex + 1).padStart(2, '0');
    });

    hero.querySelectorAll('[data-reading-hero-source]').forEach((node) => {
      if (!node.matches('[data-reading-hero-sheet]')) node.textContent = source;
    });

    hero.querySelectorAll('[data-reading-hero-title]').forEach((node) => {
      if (!node.matches('[data-reading-hero-sheet]')) node.textContent = title;
    });
  };

  const initHero = (hero) => {
    if (!hero || initializedHeroes.has(hero)) return initializedHeroes.get(hero) || null;

    const controller = new AbortController();
    const options = { signal: controller.signal };
    const selectors = [...hero.querySelectorAll('[data-reading-hero-select]')];

    selectors.forEach((selector, index) => {
      selector.addEventListener('pointerenter', () => setHeroActive(hero, index), options);
      selector.addEventListener('focus', () => setHeroActive(hero, index), options);
    });

    const api = {
      setActive: (index) => setHeroActive(hero, index),
      destroy() {
        controller.abort();
        initializedHeroes.delete(hero);
      }
    };

    initializedHeroes.set(hero, api);
    setHeroActive(hero, 0);
    return api;
  };

  const getVisibleHeaderBottom = () => {
    const main = document.getElementById('MainContent');
    const candidates = [...document.querySelectorAll('.shopify-section-group-header-group, header, [role="banner"]')]
      .filter((element, index, array) => array.indexOf(element) === index)
      .filter((element) => !main?.contains(element));

    return candidates.reduce((bottom, element) => {
      const rect = element.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return bottom;

      const position = window.getComputedStyle(element).position;
      const visuallyPinned = position === 'fixed' || position === 'sticky' || rect.top >= -1;
      return visuallyPinned ? Math.max(bottom, rect.bottom) : bottom;
    }, 0);
  };

  const initReader = (reader) => {
    if (!reader || initializedReaders.has(reader)) return initializedReaders.get(reader) || null;

    const controller = new AbortController();
    const options = { signal: controller.signal };
    const pages = [...reader.querySelectorAll('[data-reading-page-item]')];
    const excerpts = [...reader.querySelectorAll('[data-reading-excerpt]')];
    const jumpLinks = [...reader.querySelectorAll('[data-reading-jump]')];
    const pageOpeners = pages.map((page) => page.querySelector('[data-reading-viewer-open]'));
    const currentPage = reader.querySelector('[data-reading-current-page]');
    const currentSource = reader.querySelector('[data-reading-current-source]');
    const currentTitle = reader.querySelector('[data-reading-current-title]');
    const progress = reader.querySelector('[data-reading-progress]');
    const previous = reader.querySelector('[data-reading-prev-page]');
    const next = reader.querySelector('[data-reading-next-page]');
    const announcer = reader.querySelector('[data-reading-announcer]');
    const viewer = reader.querySelector('[data-reading-viewer]');
    const viewerImage = viewer?.querySelector('[data-reading-viewer-image]');
    const viewerCurrent = viewer?.querySelector('[data-reading-viewer-current]');
    const viewerSource = viewer?.querySelector('[data-reading-viewer-source]');
    const viewerTitle = viewer?.querySelector('[data-reading-viewer-title]');
    const viewerPageLabel = viewer?.querySelector('[data-reading-viewer-page-label]');
    const viewerProgress = viewer?.querySelector('[data-reading-viewer-progress]');
    const viewerPrevious = viewer?.querySelector('[data-reading-viewer-prev]');
    const viewerNext = viewer?.querySelector('[data-reading-viewer-next]');
    const viewerClose = viewer?.querySelector('[data-reading-viewer-close]');
    const ratios = new Map();
    let activePageIndex = 0;
    let viewerPageIndex = 0;
    let observer = null;
    let headerResizeObserver = null;
    let stickyFrame = 0;

    const announce = (message) => {
      if (!announcer || !message) return;
      announcer.textContent = '';
      window.requestAnimationFrame(() => {
        announcer.textContent = message;
      });
    };

    const updateStickyOffset = () => {
      stickyFrame = 0;
      const headerBottom = getVisibleHeaderBottom();
      reader.style.setProperty('--tw-reader-sticky-top', `${Math.max(8, Math.ceil(headerBottom + 8))}px`);
    };

    const scheduleStickyOffset = () => {
      if (stickyFrame) return;
      stickyFrame = window.requestAnimationFrame(updateStickyOffset);
    };

    const syncHero = (excerptIndex) => {
      document.querySelectorAll('[data-reading-hero]').forEach((hero) => {
        const heroApi = initializedHeroes.get(hero) || initHero(hero);
        heroApi?.setActive(excerptIndex);
      });
    };

    const centerJumpInRail = (activeJump, jumpScroller) => {
      if (!activeJump || !jumpScroller || jumpScroller.scrollWidth <= jumpScroller.clientWidth + 1) return;

      const maxLeft = Math.max(0, jumpScroller.scrollWidth - jumpScroller.clientWidth);
      const targetLeft = activeJump.offsetLeft - ((jumpScroller.clientWidth - activeJump.offsetWidth) / 2);
      jumpScroller.scrollTo({
        left: Math.max(0, Math.min(targetLeft, maxLeft)),
        behavior: behavior()
      });
    };

    const setActive = (index) => {
      if (!pages.length) return;

      const safeIndex = Math.max(0, Math.min(index, pages.length - 1));
      const activePage = pages[safeIndex];
      const excerptIndex = Number(activePage.dataset.readingExcerptIndex || 0);
      const source = activePage.dataset.readingSource || '';
      const title = activePage.dataset.readingTitle || '';
      activePageIndex = safeIndex;

      pages.forEach((page, pageIndex) => {
        const active = pageIndex === safeIndex;
        page.classList.toggle('is-active', active);
        if (active) page.setAttribute('aria-current', 'page');
        else page.removeAttribute('aria-current');
      });

      jumpLinks.forEach((link, linkIndex) => {
        if (linkIndex === excerptIndex) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });

      if (currentPage) currentPage.textContent = String(safeIndex + 1).padStart(2, '0');
      if (currentSource) currentSource.textContent = source;
      if (currentTitle) currentTitle.textContent = title;
      if (progress) progress.style.width = `${((safeIndex + 1) / pages.length) * 100}%`;
      if (previous) previous.disabled = safeIndex === 0;
      if (next) next.disabled = safeIndex === pages.length - 1;

      const activeJump = jumpLinks[excerptIndex];
      const jumpScroller = activeJump?.closest('[data-reading-reader]')?.querySelector('.tw-reading-reader__index');
      centerJumpInRail(activeJump, jumpScroller);

      syncHero(excerptIndex);
    };

    const scrollToPage = (index, { updateHash = false, announceChange = false } = {}) => {
      if (!pages.length) return;
      const safeIndex = Math.max(0, Math.min(index, pages.length - 1));
      const page = pages[safeIndex];
      const excerpt = page.closest('[data-reading-excerpt]');

      setActive(safeIndex);
      page.scrollIntoView({ behavior: behavior(), block: 'start' });

      if (announceChange) {
        announce(`Originalseite ${safeIndex + 1} von ${pages.length}: ${page.dataset.readingTitle || ''}`);
      }

      if (updateHash && excerpt?.id && !isDesignMode()) {
        window.history.replaceState(null, '', `#${excerpt.id}`);
      }
    };

    const preloadViewerNeighbour = (index) => {
      const opener = pageOpeners[index];
      const src = opener?.dataset.readingPageSrc;
      if (!src) return;
      const image = new Image();
      image.src = src;
    };

    const setViewerPage = (index, { announceChange = true } = {}) => {
      if (!viewer || !viewerImage || !pageOpeners.length) return;

      const safeIndex = Math.max(0, Math.min(index, pageOpeners.length - 1));
      const opener = pageOpeners[safeIndex];
      const page = pages[safeIndex];
      if (!opener || !page) return;

      viewerPageIndex = safeIndex;
      viewerImage.src = opener.dataset.readingPageSrc || '';
      viewerImage.alt = opener.dataset.readingPageAlt || '';
      if (viewerCurrent) viewerCurrent.textContent = String(safeIndex + 1).padStart(2, '0');
      if (viewerSource) viewerSource.textContent = page.dataset.readingSource || '';
      if (viewerTitle) viewerTitle.textContent = page.dataset.readingTitle || '';
      if (viewerPageLabel) viewerPageLabel.textContent = opener.dataset.readingPageLabel || '';
      if (viewerProgress) viewerProgress.style.width = `${((safeIndex + 1) / pageOpeners.length) * 100}%`;
      if (viewerPrevious) viewerPrevious.disabled = safeIndex === 0;
      if (viewerNext) viewerNext.disabled = safeIndex === pageOpeners.length - 1;

      setActive(safeIndex);
      preloadViewerNeighbour(safeIndex - 1);
      preloadViewerNeighbour(safeIndex + 1);

      if (announceChange) {
        announce(`Vollansicht, Seite ${safeIndex + 1} von ${pageOpeners.length}: ${page.dataset.readingTitle || ''}`);
      }
    };

    const cleanupViewer = ({ restoreFocus = true, alignDocument = true } = {}) => {
      document.documentElement.classList.remove('tw-reading-viewer-open');

      if (alignDocument && pages[viewerPageIndex] && !isDesignMode()) {
        pages[viewerPageIndex].scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      if (restoreFocus && pageOpeners[viewerPageIndex]?.isConnected) {
        pageOpeners[viewerPageIndex].focus({ preventScroll: true });
      }

      if (viewerImage) {
        viewerImage.removeAttribute('src');
        viewerImage.alt = '';
      }
    };

    const closeViewer = ({ restoreFocus = true, alignDocument = true } = {}) => {
      if (!viewer) return;

      if (typeof viewer.close === 'function' && viewer.open) {
        viewer.dataset.restoreFocus = String(restoreFocus);
        viewer.dataset.alignDocument = String(alignDocument);
        viewer.close();
        return;
      }

      viewer.removeAttribute('open');
      cleanupViewer({ restoreFocus, alignDocument });
    };

    const openViewer = (index) => {
      if (!viewer || !viewerImage) return;

      setViewerPage(index, { announceChange: false });
      document.documentElement.classList.add('tw-reading-viewer-open');

      if (typeof viewer.showModal === 'function') {
        if (!viewer.open) viewer.showModal();
      } else {
        viewer.setAttribute('open', '');
      }

      window.requestAnimationFrame(() => viewerClose?.focus({ preventScroll: true }));
      announce(`Vollansicht geöffnet. Seite ${viewerPageIndex + 1} von ${pageOpeners.length}.`);
    };

    jumpLinks.forEach((link, excerptIndex) => {
      link.addEventListener('click', (event) => {
        const firstPageIndex = pages.findIndex(
          (page) => Number(page.dataset.readingExcerptIndex || 0) === excerptIndex
        );
        if (firstPageIndex < 0) return;

        event.preventDefault();
        scrollToPage(firstPageIndex, { updateHash: true, announceChange: true });
      }, options);

      link.addEventListener('keydown', (event) => {
        let targetIndex = null;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') targetIndex = excerptIndex + 1;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') targetIndex = excerptIndex - 1;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = jumpLinks.length - 1;
        if (targetIndex === null) return;

        event.preventDefault();
        const normalized = Math.max(0, Math.min(targetIndex, jumpLinks.length - 1));
        jumpLinks[normalized]?.focus({ preventScroll: true });
      }, options);
    });

    previous?.addEventListener('click', () => {
      scrollToPage(activePageIndex - 1, { announceChange: true });
    }, options);

    next?.addEventListener('click', () => {
      scrollToPage(activePageIndex + 1, { announceChange: true });
    }, options);

    pageOpeners.forEach((opener, index) => {
      opener?.addEventListener('click', () => openViewer(index), options);
    });

    viewerPrevious?.addEventListener('click', () => {
      setViewerPage(viewerPageIndex - 1);
    }, options);

    viewerNext?.addEventListener('click', () => {
      setViewerPage(viewerPageIndex + 1);
    }, options);

    viewerClose?.addEventListener('click', () => closeViewer(), options);

    viewer?.addEventListener('click', (event) => {
      if (event.target === viewer) closeViewer();
    }, options);

    viewer?.addEventListener('keydown', (event) => {
      let targetIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'PageDown') targetIndex = viewerPageIndex + 1;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'PageUp') targetIndex = viewerPageIndex - 1;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = pageOpeners.length - 1;
      if (targetIndex === null) return;

      event.preventDefault();
      setViewerPage(targetIndex);
    }, options);

    viewer?.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeViewer();
    }, options);

    viewer?.addEventListener('close', () => {
      const restoreFocus = viewer.dataset.restoreFocus !== 'false';
      const alignDocument = viewer.dataset.alignDocument !== 'false';
      delete viewer.dataset.restoreFocus;
      delete viewer.dataset.alignDocument;
      cleanupViewer({ restoreFocus, alignDocument });
    }, options);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));

        let bestPage = null;
        let bestRatio = 0;
        pages.forEach((page) => {
          const ratio = ratios.get(page) || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = page;
          }
        });

        if (!bestPage || bestRatio <= 0) return;
        const index = pages.indexOf(bestPage);
        if (index >= 0 && index !== activePageIndex) setActive(index);
      }, {
        root: null,
        rootMargin: '-14% 0px -52% 0px',
        threshold: [0, .08, .2, .36, .55, .72]
      });

      pages.forEach((page) => observer.observe(page));
    }

    window.addEventListener('resize', scheduleStickyOffset, { signal: controller.signal });
    window.addEventListener('scroll', scheduleStickyOffset, { signal: controller.signal, passive: true });

    if ('ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(scheduleStickyOffset);
      const main = document.getElementById('MainContent');
      document.querySelectorAll('.shopify-section-group-header-group, header, [role="banner"]').forEach((element) => {
        if (!main?.contains(element)) headerResizeObserver.observe(element);
      });
    }

    const api = {
      scrollToExcerpt(excerptIndex) {
        const firstPageIndex = pages.findIndex(
          (page) => Number(page.dataset.readingExcerptIndex || 0) === excerptIndex
        );
        if (firstPageIndex >= 0) scrollToPage(firstPageIndex, { updateHash: true, announceChange: true });
      },
      scrollToPage,
      openViewer,
      destroy() {
        closeViewer({ restoreFocus: false, alignDocument: false });
        observer?.disconnect();
        headerResizeObserver?.disconnect();
        if (stickyFrame) window.cancelAnimationFrame(stickyFrame);
        controller.abort();
        initializedReaders.delete(reader);
      }
    };

    initializedReaders.set(reader, api);
    updateStickyOffset();
    setActive(0);

    if (window.location.hash.startsWith('#reading-excerpt-')) {
      const targetExcerpt = excerpts.findIndex((excerpt) => `#${excerpt.id}` === window.location.hash);
      if (targetExcerpt >= 0) {
        window.requestAnimationFrame(() => api.scrollToExcerpt(targetExcerpt));
      }
    }

    return api;
  };

  const bindOpeners = (scope = document) => {
    const openers = [...(scope.querySelectorAll?.('[data-reading-open]') || [])];
    if (scope.matches?.('[data-reading-open]')) openers.unshift(scope);

    openers.forEach((opener) => {
      if (boundOpeners.has(opener)) return;
      boundOpeners.add(opener);

      opener.addEventListener('click', (event) => {
        const reader = document.querySelector('[data-reading-reader]');
        const readerApi = initializedReaders.get(reader) || initReader(reader);
        if (!readerApi) return;

        event.preventDefault();
        readerApi.scrollToExcerpt(Number(opener.dataset.readingOpen || 0));
      });
    });
  };

  const initPage = (scope = document) => {
    const heroes = [...(scope.querySelectorAll?.('[data-reading-hero]') || [])];
    if (scope.matches?.('[data-reading-hero]')) heroes.unshift(scope);
    heroes.forEach(initHero);

    const scopedReader = scope.matches?.('[data-reading-reader]')
      ? scope
      : scope.querySelector?.('[data-reading-reader]');
    initReader(scopedReader || document.querySelector('[data-reading-reader]'));
    bindOpeners(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPage());
  } else {
    initPage();
  }

  document.addEventListener('shopify:section:load', (event) => initPage(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const reader = event.target.matches?.('[data-reading-reader]')
      ? event.target
      : event.target.querySelector?.('[data-reading-reader]');
    initializedReaders.get(reader)?.destroy();

    const heroes = [...(event.target.querySelectorAll?.('[data-reading-hero]') || [])];
    if (event.target.matches?.('[data-reading-hero]')) heroes.unshift(event.target);
    heroes.forEach((hero) => initializedHeroes.get(hero)?.destroy());
  });

  document.addEventListener('shopify:block:select', (event) => {
    const excerpt = event.target.closest?.('[data-reading-excerpt]');
    const reader = excerpt?.closest?.('[data-reading-reader]');
    if (!excerpt || !reader) return;

    const readerApi = initializedReaders.get(reader) || initReader(reader);
    readerApi?.scrollToExcerpt(Number(excerpt.dataset.readingExcerpt || 0));
  });
})();